import { Input, Modal, Form, Button, message, Col, Row } from "antd";
import { useDownloadBulkCvs, useGetJobApplicantById } from "../hooks/useJobs";
import { useEffect, useState } from "react";
import { getUserId } from "../helper";
// import TextArea from "antd/es/input/TextArea";
import { NeonHexagonLoader } from "./JobLoader";

const ViewApplicantModal = ({ visible, onClose, selectedApplicant }) => {
  //eslint-disable-next-line no-unused-vars
  const [error, setError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const userId = getUserId();
  const {
    getJobApplicantById,
    data: applicantData,
    loading,
  } = useGetJobApplicantById();

  const {
    downloadBulk,
    // loading: Downloading,
    // error: errorDownloading,
  } = useDownloadBulkCvs();

  // Correct useState initialization
  const [appData, setAppData] = useState({
    fullname: "",
    email: "",
    location: "",
    phone_number: "",
    company_name: "",
    position: "",
    experience_start_date: "",
    experience_end_date: "",
    // job_description: "",
    cv: null,
  });

  // Fetch application details on selectedApplicant change
  useEffect(() => {
    const fetchApplication = async (app_id) => {
      if (app_id) {
        await getJobApplicantById(app_id, userId);
      }
    };
    fetchApplication(selectedApplicant);
  }, [getJobApplicantById, selectedApplicant, userId]);

  // Update appData when applicantData changes
  useEffect(() => {
    if (applicantData) {
      setAppData({
        fullname: applicantData.user_name || "",
        email: applicantData.email || "",
        location: applicantData.location || "",
        phone_number: applicantData.phone_number || "",
        company_name: applicantData.where_did_you_work || "",
        position: applicantData.position || "",
        experience_start_date: applicantData.experience_start_date || "",
        experience_end_date: applicantData.experience_end_date || "",
        // job_description: applicantData.experience_description || "",
        cv: applicantData.cv || null,
      });
    }
  }, [applicantData]);

  // Single CV download using the bulk download API
  const downloadSingleCV = async (appId, filename) => {
    setIsDownloading(true);

    try {
      // console.log("Downloading CV for applicant ID:", appId);

      // Use the bulk download API with a single ID
      const response = await downloadBulk(appId.toString());
      console.log("Download response:", response);
      console.log("Response type:", typeof response);
      console.log("Response keys:", Object.keys(response || {}));

      // Enhanced response handling for different scenarios
      let blob = null;

      if (response instanceof Blob) {
        // Response is directly a blob
        console.log("Response is a Blob");
        blob = response;
      } else if (response.data instanceof Blob) {
        // Response.data is a blob
        console.log("Response.data is a Blob");
        blob = response.data;
      } else if (response instanceof ArrayBuffer) {
        // Response is an ArrayBuffer
        console.log("Response is ArrayBuffer");
        blob = new Blob([response], { type: "application/octet-stream" });
      } else if (response.data instanceof ArrayBuffer) {
        // Response.data is an ArrayBuffer
        console.log("Response.data is ArrayBuffer");
        blob = new Blob([response.data], { type: "application/octet-stream" });
      } else if (
        typeof response === "string" &&
        (response.startsWith("%PDF") || response.startsWith("PK"))
      ) {
        // Response is a PDF string or DOC file (DOC files often start with PK for ZIP-based formats)
        console.log("Response is document string");
        blob = new Blob([response], { type: "application/octet-stream" });
      } else if (response.data && typeof response.data === "string") {
        // Handle base64 encoded data
        console.log("Handling potential base64 data");
        try {
          // Try to decode as base64
          const binaryString = atob(response.data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: "application/octet-stream" });
        } catch (e) {
          // If base64 decode fails, treat as regular string
          blob = new Blob([response.data], {
            type: "application/octet-stream",
          });
        }
      } else if (response.url || response.data?.url) {
        // If response contains a download URL
        console.log("Response contains URL");
        const downloadUrl = response.url || response.data.url;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename || `${appData.fullname}-CV`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.success(`Successfully downloaded CV for ${appData.fullname}`);
        return; // Exit early for URL downloads
      } else {
        // Handle other response formats
        // console.warn("Unexpected response format:", response);
        // console.log("Response content:", response);
        message.error("Unexpected response format from server");
        return;
      }

      // Verify blob is valid
      if (!blob || blob.size === 0) {
        // console.error("Empty or invalid blob created");
        message.error("Received empty file from server");
        return;
      }

      // console.log("Created blob with size:", blob.size, "bytes");
      // console.log("Blob type:", blob.type);

      // Detect actual file type from blob content for more accuracy
      let actualFileType = "pdf"; // default
      try {
        actualFileType = await detectFileType(blob);
        console.log("Detected file type from content:", actualFileType);
      } catch (e) {
        console.warn("Could not detect file type from cv content");
      }
      // Download the blob
      downloadFile(blob, appData.fullname);
      message.success(`Successfully downloaded CV for ${appData.fullname}`);
    } catch (error) {
      console.error("CV download error:", error);

      // Enhanced error logging
      if (error.response) {
        // console.log("Error response status:", error.response.status);
        // console.log("Error response headers:", error.response.headers);
        // console.log("Error response data type:", typeof error.response.data);
      }

      // Handle different error types
      if (error.response?.status === 404) {
        message.error("CV not found. Please try again.");
      } else if (error.response?.status === 500) {
        message.error("Server error occurred. Please try again later.");
      } else if (error.message?.includes("Network")) {
        message.error(
          "Network error. Please check your connection and try again."
        );
      } else {
        message.error("Failed to download CV. Please try again.");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Enhanced helper function to determine file extension from URL or content
  // const getFileExtension = (url, defaultExt = "pdf") => {
  //   try {
  //     if (url && typeof url === "string") {
  //       // Extract from URL
  //       const urlParts = url.split("/");
  //       const filename = urlParts[urlParts.length - 1];
  //       const decodedFilename = decodeURIComponent(filename);

  //       if (decodedFilename.includes(".pdf")) return "pdf";
  //       if (decodedFilename.includes(".doc")) return "doc";
  //       if (decodedFilename.includes(".docx")) return "docx";
  //     }
  //     return defaultExt;
  //   } catch (e) {
  //     return defaultExt;
  //   }
  // };
  // Enhanced helper function to detect file type from blob content
  const detectFileType = (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = function () {
        const arr = new Uint8Array(this.result);
        let header = "";
        for (let i = 0; i < Math.min(4, arr.length); i++) {
          header += arr[i].toString(16);
        }

        // Check file signatures
        if (header.startsWith("25504446")) {
          // %PDF
          resolve("pdf");
        } else if (
          header.startsWith("504b0304") ||
          header.startsWith("504b0506")
        ) {
          // PK (ZIP-based files like DOCX)
          resolve("docx");
        } else if (header.startsWith("d0cf11e0")) {
          // Old DOC format
          resolve("doc");
        } else {
          // Default based on common cases
          resolve("pdf");
        }
      };
      reader.readAsArrayBuffer(blob.slice(0, 4));
    });
  };
  const downloadFile = (blob, filename) => {
    try {
      // Validate blob before creating URL
      if (!blob || blob.size === 0) {
        throw new Error("Invalid or empty blob");
      }

      console.log(`Downloading file: ${filename}, Size: ${blob.size} bytes`);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      // Ensure link is added to DOM for some browsers
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // Cleanup with a small delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error downloading file:", error);
      message.error("Failed to download file: " + error.message);
    }
  };

  const handleCVAction = async (action) => {
    // console.log("CV URL:", appData.cv);
    // console.log("Selected applicant ID:", selectedApplicant);

    if (action === "Viewing") {
      if (!appData?.cv) {
        message.warning("No CV available for viewing.");
        return;
      }
      message.info(`Opening CV for ${appData.fullname}`);
      window.open(appData.cv, "_blank", "noopener,noreferrer");
    } else if (action === "Downloading") {
      if (!appData?.cv) {
        message.warning("No CV available for download.");
        return;
      }
      if (!selectedApplicant) {
        message.warning("No applicant selected for download.");
        return;
      }

      // Create base filename without extension for more accurate detection
      let baseFilename;
      try {
        const safeName = appData.fullname
          .replace(/[^a-zA-Z0-9\s]/g, "")
          .replace(/\s+/g, "-");
        baseFilename = `${safeName}-CV`;
      } catch (e) {
        baseFilename = "CV";
      }

      // console.log("Generated filename:", baseFilename);

      // Use the API endpoint for download
      await downloadSingleCV(selectedApplicant, baseFilename);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <NeonHexagonLoader />
      </div>
    );
  }

  return (
    <Modal
      title="Applicant Information"
      visible={visible}
      onCancel={onClose}
      footer={null}
      className="custom-modal"
      width={800}
    >
      <Form layout="vertical">
        {/* Grid layout: 2 columns for fields, 1 row for TextArea */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Full Name">
              <Input value={appData?.fullname} readOnly />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Email Address">
              <Input value={appData?.email} readOnly />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Location">
              <Input value={appData?.location} readOnly />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Phone Number">
              <Input value={appData?.phone_number} readOnly />
            </Form.Item>
          </Col>
        </Row>

        {/* Experience section */}
        <div className="experience-section">
          {(appData.position ||
            appData.company_name ||
            appData.experience_start_date ||
            appData.experience_end_date) && <h4>Experience</h4>}
          <Row gutter={16}>
            {appData?.company_name && (
              <Col span={12}>
                <Form.Item label="Company Name">
                  <Input value={appData?.company_name} readOnly />
                </Form.Item>
              </Col>
            )}
            {appData?.position && (
              <Col span={12}>
                <Form.Item label="Position">
                  <Input value={appData?.position} readOnly />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row gutter={16}>
            {appData?.experience_start_date && (
              <Col span={12}>
                <Form.Item label="From">
                  <Input value={appData?.experience_start_date} readOnly />
                </Form.Item>
              </Col>
            )}
            {appData?.experience_end_date && (
              <Col span={12}>
                <Form.Item label="To">
                  <Input value={appData?.experience_end_date} readOnly />
                </Form.Item>
              </Col>
            )}
          </Row>
        </div>

        {/* CV Actions */}
        {appData?.cv && (
          <div
            style={{
              display: "flex",
              gap: "1rem",
              marginTop: "1rem",
              padding: "1rem 0",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Button
              type="default"
              style={{
                width: "50%",
                height: "40px",
              }}
              onClick={() => handleCVAction("Viewing")}
              disabled={!appData?.cv}
            >
              View CV
            </Button>
            <Button
              type="primary"
              style={{
                width: "50%",
                height: "40px",
                backgroundColor: "#08839b",
                borderColor: "#08839b",
              }}
              loading={isDownloading}
              onClick={() => handleCVAction("Downloading")}
              disabled={!selectedApplicant}
            >
              {isDownloading ? "Downloading..." : "Download CV"}
            </Button>
          </div>
        )}

        {/* CV Status Indicator */}
        {appData?.cv ? (
          <div
            style={{
              marginTop: "0.5rem",
              fontSize: "12px",
              color: "#52c41a",
            }}
          >
            ✓ CV Available
          </div>
        ) : (
          <div
            style={{
              marginTop: "0.5rem",
              fontSize: "12px",
              color: "#ff4d4f",
            }}
          >
            ✗ No CV Available
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default ViewApplicantModal;
